import { Button } from '@/components/ui/button'
import { getFormPlayerStyle } from '@/lib/form-theme'
import { cn } from '@/lib/utils'
import type { PublicForm } from '@/types/form'
import confetti from 'canvas-confetti'
import { Copy, Mail, RotateCcw } from 'lucide-react'
import { motion } from 'motion/react'
import { useEffect } from 'react'
import { BrandBanner, BrandChrome } from './BrandChrome'

export function ThankYouScreen({
  form,
  onRestart,
  previewMode = false,
}: {
  form: PublicForm
  onRestart: () => void
  previewMode?: boolean
}) {
  const thankYouScreen = form.thankYouScreen
  const animationStyle = thankYouScreen.animationStyle ?? 'check_confetti'

  useEffect(() => {
    if (animationStyle === 'check_confetti') {
      void confetti({ particleCount: 120, spread: 80, origin: { y: 0.65 } })
    }
  }, [animationStyle])

  useEffect(() => {
    if (!thankYouScreen.redirectUrl) return
    const delay = Math.max(0, thankYouScreen.redirectDelaySeconds ?? 5) * 1000
    const timeoutId = window.setTimeout(() => {
      window.location.assign(thankYouScreen.redirectUrl as string)
    }, delay)
    return () => window.clearTimeout(timeoutId)
  }, [thankYouScreen.redirectDelaySeconds, thankYouScreen.redirectUrl])

  const primaryCta =
    thankYouScreen.showPrimaryCta === true &&
    thankYouScreen.primaryCtaLabel &&
    thankYouScreen.primaryCtaUrl
      ? { label: thankYouScreen.primaryCtaLabel, url: thankYouScreen.primaryCtaUrl }
      : null
  const secondaryCta =
    thankYouScreen.showSecondaryCta === true &&
    thankYouScreen.secondaryCtaLabel &&
    thankYouScreen.secondaryCtaUrl
      ? { label: thankYouScreen.secondaryCtaLabel, url: thankYouScreen.secondaryCtaUrl }
      : null
  const showNewSubmissionButton = thankYouScreen.showNewSubmissionButton !== false

  return (
    <div
      className={cn(
        'form-player-theme relative grid place-items-center overflow-y-auto p-6 py-20 text-center',
        previewMode ? 'min-h-full' : 'min-h-screen',
        animationStyle === 'spotlight' && 'overflow-x-hidden',
      )}
      style={getFormPlayerStyle(form.themeConfig, form.settings)}
    >
      <BrandChrome form={form} contained={previewMode} />
      <motion.div
        className={cn(
          'mx-auto w-full',
          animationStyle === 'card_stack' && 'border border-white/50 p-8 shadow-xl backdrop-blur',
        )}
        style={{
          background:
            animationStyle === 'card_stack'
              ? 'color-mix(in srgb, var(--form-bg,#F8FAFC) 82%, white)'
              : undefined,
          borderRadius:
            animationStyle === 'card_stack' ? 'calc(var(--form-radius,8px) + 8px)' : undefined,
          color: 'var(--form-text,#0F172A)',
          maxWidth: 'min(var(--form-max-width,720px), 640px)',
          textAlign: 'center',
        }}
        initial={animationStyle === 'minimal' ? false : { y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: animationStyle === 'spotlight' ? 0.5 : 0.35 }}
      >
        <BrandBanner form={form} surface="thankyou" />
        {animationStyle !== 'minimal' && (
          <svg
            aria-labelledby="thank-you-check-title"
            className="mx-auto size-20"
            viewBox="0 0 64 64"
            fill="none"
            style={{ color: 'var(--form-accent,#10B981)' }}
          >
            <title id="thank-you-check-title">提交完成</title>
            <motion.path
              d="M18 33L28 43L48 21"
              stroke="currentColor"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.6 }}
            />
          </svg>
        )}
        <h1
          className="mt-6 font-semibold"
          style={{
            fontFamily: 'var(--form-heading-font,var(--form-font,Geist))',
            fontSize: 'var(--form-question-size,2.25rem)',
            lineHeight: 'calc(var(--form-line-height,1.55) - 0.28)',
          }}
        >
          {thankYouScreen.title}
        </h1>
        <p
          className="mt-4"
          style={{
            color: 'var(--form-answer,#475569)',
            fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
            fontSize: 'var(--form-body-size,1.125rem)',
            lineHeight: 'var(--form-line-height,1.55)',
          }}
        >
          {thankYouScreen.description}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {showNewSubmissionButton && (
            <Button
              type="button"
              onClick={onRestart}
              style={{
                background: 'var(--form-button-a11y,var(--form-button,#6366F1))',
                borderRadius: 'var(--form-radius,8px)',
                color: 'var(--form-button-text,#FFFFFF)',
              }}
            >
              <RotateCcw className="size-4" />
              {thankYouScreen.newSubmissionButtonLabel ?? '再次填寫新問卷'}
            </Button>
          )}
          {primaryCta && (
            <Button
              asChild
              style={{
                background: 'var(--form-button-a11y,var(--form-button,#6366F1))',
                borderRadius: 'var(--form-radius,8px)',
                color: 'var(--form-button-text,#FFFFFF)',
              }}
            >
              <a href={primaryCta.url}>{primaryCta.label}</a>
            </Button>
          )}
          {secondaryCta && (
            <Button
              asChild
              variant="outline"
              style={{
                background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 82%, white)',
                borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 32%, transparent)',
                borderRadius: 'var(--form-radius,8px)',
                color: 'var(--form-text,#0F172A)',
              }}
            >
              <a href={secondaryCta.url}>{secondaryCta.label}</a>
            </Button>
          )}
          {Boolean(form.settings.allowResubmit) && thankYouScreen.showResubmitButton === true && (
            <Button
              variant="outline"
              onClick={onRestart}
              style={{
                background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 82%, white)',
                borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 32%, transparent)',
                borderRadius: 'var(--form-radius,8px)',
                color: 'var(--form-text,#0F172A)',
              }}
            >
              重新填寫
            </Button>
          )}
        </div>
        {thankYouScreen.showShareButtons === true && (
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void navigator.clipboard?.writeText(window.location.href)}
              style={{
                background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 82%, white)',
                borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 32%, transparent)',
                borderRadius: 'var(--form-radius,8px)',
                color: 'var(--form-text,#0F172A)',
              }}
            >
              <Copy className="size-4" />
              複製連結
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              style={{
                background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 82%, white)',
                borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 32%, transparent)',
                borderRadius: 'var(--form-radius,8px)',
                color: 'var(--form-text,#0F172A)',
              }}
            >
              <a
                href={`mailto:?subject=${encodeURIComponent(form.title)}&body=${encodeURIComponent(
                  window.location.href,
                )}`}
              >
                <Mail className="size-4" />
                Email
              </a>
            </Button>
          </div>
        )}
        {thankYouScreen.redirectUrl && (
          <div
            className="mt-5 px-3 py-2 text-sm"
            style={{
              background: 'color-mix(in srgb, var(--form-accent,#6366F1) 12%, transparent)',
              borderRadius: 'var(--form-radius,8px)',
              color: 'var(--form-answer,#475569)',
            }}
          >
            {thankYouScreen.redirectDelaySeconds ?? 5} 秒後自動導向下一頁。
          </div>
        )}
      </motion.div>
    </div>
  )
}
